import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  CheckCircle,
  Lock,
  Scales,
  Shield,
  UsersThree,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
const SENSITIVE_BADGE_ICONS = {
  'Finance': { icon: Lock, color: 'bg-orange-100 text-orange-700 border-orange-300' },
  'HR': { icon: UsersThree, color: 'bg-blue-100 text-blue-700 border-blue-300' },
  'Legal': { icon: Scales, color: 'bg-purple-100 text-purple-700 border-purple-300' },
  'Compliance': { icon: Shield, color: 'bg-green-100 text-green-700 border-green-300' }
};

const AgentAccessManager = ({ userId, userName, userRole }) => {
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [accessMap, setAccessMap] = useState({});
  const [originalAccessMap, setOriginalAccessMap] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (activeOrganization) {
      fetchAgentAccess();
    }
  }, [userId, activeOrganization]);

  const fetchAgentAccess = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/admin/users/${userId}/agent-access`,
        {},
        activeOrganization.organization_id
      );

      if (!response.ok) {
        throw new Error('Failed to fetch agent access');
      }

      const data = await response.json();
      
      if (data.is_admin) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      setAgents(data.agents || []);
      
      // Build access map
      const map = {};
      data.agents.forEach(agent => {
        map[agent.agent_id] = agent.has_access;
      });
      
      setAccessMap(map);
      setOriginalAccessMap({ ...map });
      setIsAdmin(false);
      
    } catch (err) {
      console.error('Error fetching agent access:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (agentId) => {
    setAccessMap(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  const hasChanges = () => {
    return Object.keys(accessMap).some(
      agentId => accessMap[agentId] !== originalAccessMap[agentId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Build updates array
      const updates = Object.keys(accessMap)
        .filter(agentId => accessMap[agentId] !== originalAccessMap[agentId])
        .map(agentId => ({
          agent_id: agentId,
          has_access: accessMap[agentId]
        }));

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/admin/users/${userId}/agent-access`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            updates: updates
          })
        },
        activeOrganization.organization_id
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update agent access');
      }

      const result = await response.json();
      
      setSuccess(`Successfully updated access: ${result.granted_count} granted, ${result.revoked_count} revoked`);
      setOriginalAccessMap({ ...accessMap });
      
      // Refresh data
      setTimeout(() => {
        fetchAgentAccess();
        setSuccess(null);
      }, 2000);
      
    } catch (err) {
      console.error('Error saving agent access:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAccessMap({ ...originalAccessMap });
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading agent access...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Agent Access
          </CardTitle>
          <CardDescription>Manage which agents this user can run</CardDescription>
        </CardHeader>
        <CardContent>
          <WarningCircle>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>{userName}</strong> is an admin and has access to all agents by default.
              Admin permissions cannot be modified here.
            </AlertDescription>
          </WarningCircle>
        </CardContent>
      </Card>
    );
  }

  const accessibleCount = Object.values(accessMap).filter(Boolean).length;
  const totalCount = agents.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              Agent Access
            </CardTitle>
            <CardDescription>
              Control which agents {userName} can run (Privacy-First)
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">{accessibleCount}</div>
            <div className="text-sm text-gray-500">of {totalCount} agents</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Privacy Notice */}
        <Alert className="bg-blue-50 border-blue-200">
          <Shield className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-900">
            <strong>Privacy-First by Design:</strong> Access is explicit. No data is shared without permission.
          </AlertDescription>
        </Alert>

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="destructive">
            <WarningCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-900">{success}</AlertDescription>
          </Alert>
        )}

        {/* Agent List */}
        {agents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Lock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No agents available in this organization</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {agents.map((agent) => {
              const sensitiveBadge = agent.sensitive_badge;
              const BadgeIcon = sensitiveBadge ? SENSITIVE_BADGE_ICONS[sensitiveBadge.label]?.icon : null;
              const badgeColor = sensitiveBadge ? SENSITIVE_BADGE_ICONS[sensitiveBadge.label]?.color : '';

              return (
                <div
                  key={agent.agent_id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    accessMap[agent.agent_id]
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  } hover:shadow-sm transition-all`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Checkbox
                      id={`agent-${agent.agent_id}`}
                      checked={accessMap[agent.agent_id] || false}
                      onCheckedChange={() => handleToggle(agent.agent_id)}
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor={`agent-${agent.agent_id}`}
                          className="font-medium text-gray-900 cursor-pointer"
                        >
                          {agent.agent_name}
                        </label>
                        
                        {sensitiveBadge && BadgeIcon && (
                          <Badge className={`${badgeColor} border flex items-center gap-1 text-xs`}>
                            <BadgeIcon className="w-3 h-3" />
                            {sensitiveBadge.label}
                          </Badge>
                        )}
                        
                        {agent.status === 'draft' && (
                          <Badge variant="outline" className="text-xs">
                            Draft
                          </Badge>
                        )}
                      </div>
                      
                      {agent.department && (
                        <div className="text-sm text-gray-500 mt-0.5">
                          {agent.department}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-3">
                    {accessMap[agent.agent_id] ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        {agents.length > 0 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-gray-600">
              {hasChanges() && (
                <span className="text-orange-600 font-medium">
                  Unsaved changes
                </span>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges() || saving}
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges() || saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentAccessManager;
